import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  CircularProgress,
  ButtonGroup,
} from "@mui/material";
import { TrendingUp, People } from "@mui/icons-material";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Label,
} from "recharts";
import SingleDatePicker from "../utils/singleDate";
import { format } from "date-fns";
import { useEntryTrafficSocket } from "../hooks/useEntryTrafficSocket";

// Generate hourly time slots based on time range
const generateHourlySlots = (range) => {
  const slots = [];
  if (range === '24h') {
    // 00:00 to 23:00 (all 24 hours)
    for (let h = 0; h < 24; h++) {
      const hour = h.toString().padStart(2, '0');
      slots.push(`${hour}:00`);
    }
  } else if (range === '12h') {
    // 09:00 to 21:00 (9 AM to 9 PM)
    for (let h = 9; h <= 21; h++) {
      const hour = h.toString().padStart(2, '0');
      slots.push(`${hour}:00`);
    }
  } else if (range === '6h') {
    // 12:00 to 18:00 (12 PM to 6 PM)
    for (let h = 12; h <= 18; h++) {
      const hour = h.toString().padStart(2, '0');
      slots.push(`${hour}:00`);
    }
  }
  return slots;
};

// Convert time string (HH:mm) to numeric position (hours as decimal)
const timeToPosition = (timeStr, startHour = 0) => {
  const [h, m] = timeStr.split(':').map(Number);
  const totalHours = h + m / 60;
  return totalHours - startHour; // Normalize to start from 0
};

// Convert numeric position back to time string for display
const positionToTime = (position, startHour = 0) => {
  const totalHours = position + startHour;
  const h = Math.floor(totalHours);
  const m = Math.round((totalHours - h) * 60);
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
};

// Get current time in HH:mm format
const getCurrentTime = () => {
  const now = new Date();
  const hours = now.getHours().toString().padStart(2, '0');
  const minutes = now.getMinutes().toString().padStart(2, '0');
  return `${hours}:${minutes}`;
};

// Check if a date is today
const isToday = (date) => {
  if (!date) return false;
  const today = new Date();
  const checkDate = new Date(date);
  return (
    checkDate.getDate() === today.getDate() &&
    checkDate.getMonth() === today.getMonth() &&
    checkDate.getFullYear() === today.getFullYear()
  );
};

// Check if a time string is before or equal to current time
const isTimeBeforeOrEqual = (timeStr, compareTime) => {
  const [h1, m1] = timeStr.split(':').map(Number);
  const [h2, m2] = compareTime.split(':').map(Number);
  const time1 = h1 * 60 + m1;
  const time2 = h2 * 60 + m2;
  return time1 <= time2;
};

// Process API data with minute precision for stepped line chart
const merge24HourData = (apiData) => {
  if (!apiData || apiData.length === 0) return [];
  
  // Sort API data by time to ensure chronological order
  const sortedData = [...apiData].sort((a, b) => {
    const [h1, m1] = a.time.split(':').map(Number);
    const [h2, m2] = b.time.split(':').map(Number);
    if (h1 !== h2) return h1 - h2;
    return m1 - m2;
  });
  
  // Return data with minute precision (preserve original time format from API)
  return sortedData.map(item => ({
    time: item.time, // Keep original "HH:mm" format from API
    count: item.count || 0
  }));
};

const EmployeeMovementTooltip = ({ active, payload, label, startHour }) => {
  if (!active || !payload || payload.length === 0) return null;

  const point = payload[0].payload || {};
  const timeLabel = point.time || positionToTime(label, startHour);
  const count = payload[0].value ?? point.count ?? 0;

  return (
    <Box
      sx={{
        background: "#fff",
        border: "1px solid #ccc",
        borderRadius: "8px",
        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
        fontSize: window.innerWidth < 600 ? '11px' : '14px',
        maxWidth: 280,
        p: 1.25
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 600, color: '#333' }}>
        Time: {timeLabel}
      </Typography>
      <Typography variant="body2" sx={{ color: '#40518A', mt: 0.5 }}>
        Count: {count} employees
      </Typography>
    </Box>
  );
};

// Filter data based on specific time ranges (works with minute-precision data)
const filterByTimeRange = (data, range) => {
  if (range === '24h') {
    // 00:00 to 23:59 (all 24 hours)
    return data;
  } else if (range === '12h') {
    // 09:00 to 21:59 (9 AM to 9:59 PM)
    return data.filter((item) => {
      const [h, m] = item.time.split(":").map(Number);
      const timeInMinutes = h * 60 + m;
      return timeInMinutes >= 9 * 60 && timeInMinutes <= 21 * 60 + 59;
    });
  } else if (range === '6h') {
    // For 6h: Show data from current time to next 6 hours
    // This will be handled in the component with current time context
    return data; // Return all data, filtering will be done with current time context
  }
  return data;
};

// Calculate dynamic Y-axis domain (never negative)
const calculateYAxisDomain = (data) => {
  if (!data || data.length === 0) return [0, 10];
  
  const maxCount = Math.max(...data.map(item => item.count));
  
  // Ensure minimum is always 0 (never negative)
  const minValue = 0;
  
  // Add 15% padding to top for better visualization
  const padding = Math.ceil(maxCount * 0.15) || 2;
  const upperLimit = Math.max(10, maxCount + padding);
  
  return [minValue, upperLimit];
};

const generateYAxisTicks = (domain) => {
  const [min, max] = domain;
  const ticks = [];
  const step = Math.max(1, Math.ceil((max - min) / 8)); // Generate ~8 ticks
  for (let i = min; i <= max; i += step) {
    ticks.push(i);
  }
  // Always include the max value
  if (ticks[ticks.length - 1] !== max) {
    ticks.push(max);
  }
  return ticks;
};

// - Reusable component
const LineChartCard = ({
  title = "Employee Presence Over Time",
  lineColor = "#40518A",
  height = 400,
  maxWidth = "100%",
  xAxisLabelInterval = null, // Configurable: null = auto, number = fixed interval
}) => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [committedDate, setCommittedDate] = useState(new Date());
  const [fullChartData, setFullChartData] = useState([]); // Full 24-hour data
  const [displayData, setDisplayData] = useState([]); // Filtered data for display
  const [timeRange, setTimeRange] = useState('24h'); // Default to 24h view (0 AM - 23 PM)
  const [pickerError, setPickerError] = useState(null);
  const scrollContainerRef = useRef(null);
  const [currentTimeState, setCurrentTimeState] = useState(getCurrentTime()); // For real-time updates

  const committedDateStr = format(committedDate, "yyyy-MM-dd");
  const {
    timeSeries,
    peakOccupancy,
    currentPresent,
    loading,
    error,
    refresh,
  } = useEntryTrafficSocket({ date: committedDateStr });

  useEffect(() => {
    setFullChartData(merge24HourData(timeSeries ?? []));
  }, [timeSeries]);

  // Update display data when time range or full data changes
  useEffect(() => {
    // Always use startHour = 0 for position calculations (positions are from midnight)
    // This ensures x-axis labels match data point positions correctly
    const startHour = 0;
    const isTodayDate = isToday(committedDate);
    const currentTime = isTodayDate ? currentTimeState : getCurrentTime(); // Use real-time state for today
    
    // If no data, create empty data points at hourly intervals
    if (fullChartData.length === 0) {
      const hourlySlots = generateHourlySlots(timeRange); // Use selected time range
      
      // Always start from position 0 (midnight)
      const emptyData = [{
        time: "00:00",
        position: 0,
        count: 0
      }];
      
      // For today, only show slots up to current time
      const slotsToShow = isTodayDate 
        ? hourlySlots.filter(time => isTimeBeforeOrEqual(time, currentTime))
        : hourlySlots;
      
      // Add hourly slots
      slotsToShow.forEach(time => {
        const position = timeToPosition(time, startHour);
        emptyData.push({
          time: time, // Keep original time for tooltip
          position: position, // Numeric position for X-axis
          count: 0
        });
      });
      
      // For today, always add current time point
      if (isTodayDate) {
        const currentPosition = timeToPosition(currentTime, startHour);
        emptyData.push({
          time: currentTime,
          position: currentPosition,
          count: 0
        });
      }
      
      setDisplayData(emptyData);
      return;
    }

    // Filter data based on time range
    // For all time ranges (6h, 12h, 24h): Use same filtering logic
    const filtered = filterByTimeRange(fullChartData, timeRange);
    
    // For today, filter out future data points (only show up to current time)
    const filteredUpToCurrent = !isTodayDate
      ? filtered
      : filtered.filter(item => isTimeBeforeOrEqual(item.time, currentTime));
    
    // Convert all data points to include numeric positions
    // This allows constant spacing between hours regardless of data point count
    const dataWithPositions = filteredUpToCurrent.map(item => {
      const position = timeToPosition(item.time, startHour);
      return {
        time: item.time, // Keep original time for tooltip
        position: position, // Numeric position for X-axis
        count: item.count
      };
    });
    
    // Sort by position to maintain chronological order
    let sortedData = dataWithPositions.sort((a, b) => a.position - b.position);
    
    // Start the line from position 0 (midnight/12 AM) with count 0
    const hourlySlots = generateHourlySlots(timeRange);
    // Always start from position 0 (midnight)
    const startPosition = 0;
    const startTime = "00:00"; // Midnight
    
    // Calculate end position based on time range
    let endPosition;
    if (timeRange === '6h' && isTodayDate) {
      // For 6h: Step line should end at current time (not current time + 6)
      const currentPosition = timeToPosition(currentTime, startHour);
      endPosition = currentPosition;
    } else {
      // For other ranges: use current time or end of time range, whichever is earlier (for today)
      const timeRangeEndPosition = timeToPosition(hourlySlots[hourlySlots.length - 1], startHour);
      const endOfDayPosition = 23 + 59/60; // 11:59 PM
      const maxEndPosition = Math.min(timeRangeEndPosition, endOfDayPosition);
      endPosition = isTodayDate
        ? Math.min(timeToPosition(currentTime, startHour), maxEndPosition)
        : maxEndPosition;
    }
    
    // Check if we need to add a starting point at position 0
    const hasStartPoint = sortedData.length > 0 && Math.abs(sortedData[0].position - startPosition) < 0.001;
    
    if (!hasStartPoint) {
      // Add starting point at position 0
      sortedData.unshift({
        time: startTime,
        position: startPosition,
        count: 0
      });
    }
    
    // If no data at all, add starting and ending points at 0
    if (sortedData.length === 0) {
      sortedData.push({
        time: startTime,
        position: startPosition,
        count: 0
      });
    }
    
    // Fill gaps with last known count to show constant line when no API hits occur
    if (sortedData.length > 0) {
      const filledData = [...sortedData];
      
      // For each data point, check if there's a significant gap before the next point
      // If gap is > 1 hour, add a point at the end of the gap to maintain constant line
      for (let i = 0; i < sortedData.length - 1; i++) {
        const currentPoint = sortedData[i];
        const nextPoint = sortedData[i + 1];
        const gap = nextPoint.position - currentPoint.position;
        
        // If gap is more than 1 hour, add a point right before next point to show constant line
        if (gap > 1) {
          // Add a point just before the next point (1 minute before) with current count
          const gapEndPosition = nextPoint.position - (1/60); // 1 minute before next point
          const [h, m] = positionToTime(gapEndPosition, startHour).split(':').map(Number);
          filledData.push({
            time: `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`,
            position: gapEndPosition,
            count: currentPoint.count // Maintain last known count
          });
        }
      }
      
      // Ensure we have a point at the end of the time range with the last known count
      const lastDataPoint = sortedData[sortedData.length - 1];
      let lastPosition = filledData[filledData.length - 1]?.position || lastDataPoint.position;
      
      // For today, always add/update a point at current time to extend step line to current time on reload
      if (isTodayDate) {
        const currentPosition = timeToPosition(currentTime, startHour);
        
        // Remove any existing point at or very close to current time (within 5 seconds) to avoid duplicates
        // Using 5 seconds threshold to allow second-by-second updates while preventing exact duplicates
        const filteredFilledData = filledData.filter(item => {
          const posDiff = Math.abs(item.position - currentPosition);
          return posDiff > 5/3600; // Remove if within 5 seconds of current time (5/3600 hours)
        });
        
        // Always add a point at current time with the last known count
        // This ensures step line extends to current time on every reload and updates every second
        filteredFilledData.push({
          time: currentTime, // Use actual current time with minutes and seconds
          position: currentPosition,
          count: lastDataPoint.count // Maintain last known count
        });
        
        filledData.length = 0; // Clear array
        filledData.push(...filteredFilledData); // Add filtered data back
        lastPosition = currentPosition; // Update last position to current time
      }
      
      // Also ensure we have a point at endPosition (for time range boundaries)
      const maxPosition = Math.max(lastPosition, isTodayDate ? timeToPosition(currentTime, startHour) : lastPosition);
      if (maxPosition < endPosition) {
        const [h, m] = positionToTime(endPosition, startHour).split(':').map(Number);
        filledData.push({
          time: `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`,
          position: endPosition,
          count: lastDataPoint.count // Maintain last known count
        });
      }
      
      // Remove duplicates and ensure current time point is always included
      const uniqueData = [];
      const seenPositions = new Set();
      
      // Get current time position for today's date
      let currentTimePosition = null;
      let currentTimePointToAdd = null;
      
      if (isTodayDate) {
        currentTimePosition = timeToPosition(currentTime, startHour);
        // Create the current time point that we want to ensure is included
        currentTimePointToAdd = {
          time: currentTime,
          position: currentTimePosition,
          count: lastDataPoint ? lastDataPoint.count : 0
        };
      }
      
      // Process all data points, but exclude any that are too close to current time
      filledData.forEach(item => {
        // Use 3 decimal places for better precision (preserves minutes accurately)
        const roundedPos = Math.round(item.position * 1000) / 1000;
        
        // Skip points that are very close to current time (we'll add the exact current time point later)
        if (currentTimePosition !== null && Math.abs(item.position - currentTimePosition) < 0.01) {
          return; // Skip this point, we'll add the exact current time point
        }
        
        if (!seenPositions.has(roundedPos)) {
          seenPositions.add(roundedPos);
          uniqueData.push(item);
        }
      });
      
      // ALWAYS add the current time point at the end for today's date
      if (isTodayDate && currentTimePointToAdd) {
        uniqueData.push(currentTimePointToAdd);
      }
      
      sortedData = uniqueData.sort((a, b) => a.position - b.position);
    }
    
    setDisplayData(sortedData);
  }, [timeRange, fullChartData, committedDate, currentTimeState]);

  // Real-time updates: extend step line to current time for today's date.
  useEffect(() => {
    const isTodayDate = isToday(committedDate);
    if (!isTodayDate) return; // Only update for today's date

    // Update current time immediately
    setCurrentTimeState(getCurrentTime());

    // Update current time every second for smooth real-time step line extension
    const timeUpdateInterval = setInterval(() => {
      const newCurrentTime = getCurrentTime();
      setCurrentTimeState(newCurrentTime);
    }, 1000); // Update every 1 second for real-time step line movement

    return () => {
      clearInterval(timeUpdateInterval);
    };
  }, [committedDate]);

  // Update current time state on mount to ensure step line extends to current time on reload
  useEffect(() => {
    setCurrentTimeState(getCurrentTime());
  }, []);

  // Auto-scroll for 12h and 6h views
  useEffect(() => {
    if (!scrollContainerRef.current || loading || displayData.length === 0) return;
    
    if (timeRange === '12h') {
      // Scroll to show 9 AM - 9 PM section (position 9 = 9 AM)
      // This positions the chart so 9 AM - 9 PM is visible, with 12 AM - 9 AM scrollable on left
      // and 9 PM - 12 AM scrollable on right
      setTimeout(() => {
        if (scrollContainerRef.current) {
          const screenWidth = window.innerWidth;
          const availableWidth = screenWidth - 200;
          
          // Calculate width per hour (same logic as in renderChartContent for 12h)
          let widthPerHour = Math.floor(availableWidth / 29);
          // Reduced minimum widths to match the reduced label gap
          const minWidth = screenWidth < 600 ? 25 : screenWidth < 900 ? 30 : 35;
          widthPerHour = Math.max(minWidth, widthPerHour);
          
          // Scroll to position 9 (9 AM) - this shows 9 AM - 9 PM in viewport
          // X-axis will show 9:00 AM as the first visible label
          const scrollPosition = 8.8 * widthPerHour;
          scrollContainerRef.current.scrollLeft = scrollPosition;
        }
      }, 200);
    } else if (timeRange === '6h') {
      // Scroll to show current hour to next 6 hours
      setTimeout(() => {
        if (scrollContainerRef.current) {
          const screenWidth = window.innerWidth;
          const availableWidth = screenWidth - 200; 
          const isTodayDate = isToday(committedDate);
          const currentTime = isTodayDate ? currentTimeState : getCurrentTime();
          
          const [currentH] = currentTime.split(':').map(Number);
         
          // Scroll to current hour (ignore minutes)
          const scrollTargetPosition = currentH; 
          
          // Calculate width per hour (same as renderChartContent)
          let widthPerHour = Math.floor(availableWidth / 14); 
          const minWidth = screenWidth < 600 ? 20 : screenWidth < 900 ? 25 : 30;
          widthPerHour = Math.max(minWidth, widthPerHour);
          
          // Scroll to the start of the current hour
          const scrollPosition = Math.max(0, scrollTargetPosition * widthPerHour);
          scrollContainerRef.current.scrollLeft = scrollPosition;
        }
      }, 200);
    } else {
      // For 24h view, reset scroll to start
      setTimeout(() => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollLeft = 0;
        }
      }, 100);
    }
  }, [displayData, loading, timeRange, currentTimeState, committedDate]);

  const handleApply = () => {
    const dateToApply = format(selectedDate, "yyyy-MM-dd");
    setCommittedDate(selectedDate);
    refresh(dateToApply, { showLoading: true });
  };

  const handlePickerChange = (date) => {
    if (!date || Number.isNaN(date.getTime())) return;
    
    // - Prevent selecting future dates
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    
    if (date > today) {
      setPickerError("Cannot select future dates");
      return;
    }
    
    setPickerError(null);
    setSelectedDate(date);
  };

  const handleTimeRangeChange = (range) => {
    setTimeRange(range);
  };

  const renderChartContent = () => {
    if (loading) {
      return (
        <Box
          sx={{
            height: 400,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    // Always display the graph, even if no data (will show empty graph with hourly labels)

    // Calculate dynamic Y-axis range (never negative)
    const yAxisDomain = calculateYAxisDomain(displayData);

    // Use time range for calculations (startHour always 0 for position calculations)
    const startHour = 0; // Always 0 since positions are calculated from midnight
    // eslint-disable-next-line no-unused-vars
    const _hourlySlots = generateHourlySlots(timeRange);
    // eslint-disable-next-line no-unused-vars
    const _isTodayDate = isToday(committedDate);
    // eslint-disable-next-line no-unused-vars
    const _currentTime = _isTodayDate ? currentTimeState : getCurrentTime(); // Use real-time state for today
    
    // Calculate x-axis domain based on time range
    // Last time shown should be 11:59 (23.983 hours in decimal)
    const endOfDayPosition = 23 + 59/60; // 11:59 PM = 23.983 hours
    
    let xAxisDomain;
    if (timeRange === '6h') {
      // The domain needs to be full range for scrolling, auto-scroll will position to current time
      xAxisDomain = [0, endOfDayPosition];
    } else if (timeRange === '12h') {
      // For 12h: Full 24 hours domain (for scrolling), ending at 11:59
      xAxisDomain = [0, endOfDayPosition];
    } else {
      // For 24h: Show all 24 hours ending at 11:59
      xAxisDomain = [0, endOfDayPosition];
    }
    
    // Calculate width per hour based on time range
    const getWidthPerHour = () => {
      const screenWidth = window.innerWidth;
      const availableWidth = screenWidth - 200; // Account for margins, Y-axis, and padding

      if (timeRange === '12h') {
        // For 12h: Calculate width to fit 12 hours (9 AM - 9 PM) in viewport with reduced gap
        const calculatedWidth = Math.floor(availableWidth / 29);
        // Reduced minimum widths to decrease label gap
        const minWidth = screenWidth < 600 ? 25 : screenWidth < 900 ? 30 : 35;
        return Math.max(minWidth, calculatedWidth);
      } else if (timeRange === '6h') {
        // For 6h: Calculate width to fit 6 hours in viewport with 50% reduced label gap
        const calculatedWidth = Math.floor(availableWidth / 14);
        // Reduced minimum widths by 50% to decrease label gap
        const minWidth = screenWidth < 600 ? 20 : screenWidth < 900 ? 20 : 25;
        return Math.max(minWidth, calculatedWidth);
      } else {
        // For 24h: Calculate width to fit all 24 hours in viewport
        const calculatedWidth = Math.floor(availableWidth / 24);
        const minWidth = screenWidth < 600 ? 30 : screenWidth < 900 ? 35 : 40;
        return Math.max(minWidth, calculatedWidth);
      }
    };

    // Calculate chart width
    const widthPerHour = getWidthPerHour();
    
    // For 12h and 6h: Full 24 hours width (scrollable), viewport shows respective hours at a time
    // All 24 hour labels are visible, but viewport displays respective consecutive hours while scrolling
    // For 24h: Show all 24 hours and fit in viewport
    const minChartWidth = (timeRange === '12h' || timeRange === '6h')
      ? 24 * widthPerHour  // Full 24 hours width for scrolling (viewport shows respective hours)
      : 24 * widthPerHour;  // 24 hours width for 24h view

    return (
      <Box 
        sx={{ 
          maxWidth: "100%",
          height: { xs: 300, sm: 300, md: 300 },
          position: 'relative',
          display: 'flex',
              /*  REQUIRED FOR MOBILE SCROLL */
          overflowX: 'hidden',
          overflowY: 'hidden',
          outline: 'none',
          border: 'none',
          '&:focus': {
            outline: 'none',
            border: 'none',
            boxShadow: 'none',
          },
          '&:focus-visible': {
            outline: 'none',
            border: 'none',
            boxShadow: 'none',
          },
        }}
        tabIndex={-1}
      >
        {/* Fixed Y-Axis Container */}
        <Box
          sx={{
            width: { xs: 45, sm: 55, md: 60 },
            flexShrink: 0,
            position: 'relative',
            zIndex: 10,
            bgcolor: 'background.paper',
            borderRight: '1px solid',
            borderColor: 'divider',
            outline: 'none',
            '&:focus': {
              outline: 'none',
            },
            '&:focus-visible': {
              outline: 'none',
            },
          }}
        >
          <ResponsiveContainer width="100%" height="100%">
            <LineChart 
              data={displayData}
              margin={{ 
                top: 15, 
                right: 0, 
                left: 5, 
                bottom: 45
              }}
            >
              <YAxis 
                yAxisId="left"
                orientation="left"
                type="number"
                ticks={generateYAxisTicks(yAxisDomain)} 
                tick={{ 
                  fontSize: window.innerWidth < 600 ? 9 : 11, 
                  fill: '#666', 
                  fontWeight: 500 
                }}
                domain={yAxisDomain}
                allowDecimals={false}
                stroke="#999"
                width={window.innerWidth < 600 ? 40 : 50}
                axisLine={true}
                tickLine={true}
                tickSize={5}
              >
                <Label
                  value="Employees"
                  angle={-90}
                  position="insideLeft"
                  offset={0}
                  style={{ 
                    fill: "#444", 
                    fontSize: window.innerWidth < 600 ? "11px" : "13px", 
                    fontWeight: 600, 
                    paddingLeft: 3,
                  }}
                />
              </YAxis>
              {/* Invisible dummy line to force Y-axis rendering */}
              <Line 
                yAxisId="left"
                type="monotone" 
                dataKey="count" 
                stroke="transparent" 
                dot={false}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>

        {/* Chart Container - Scrollable for 12h and 6h, fits viewport for 24h */}
        <Box
          ref={scrollContainerRef}
          sx={{
            flex: 1,
            minWidth: 0,
            overflowX: (timeRange === '12h' || timeRange === '6h') ? 'auto' : 'hidden',
            overflowY: 'hidden',
            position: 'relative',
            WebkitOverflowScrolling: 'touch',
            touchAction: (timeRange === '12h' || timeRange === '6h') ? 'pan-x' : 'none',
            outline: 'none',
            border: 'none',
            '&:focus': {
              outline: 'none',
              border: 'none',
              boxShadow: 'none',
            },
            '&:focus-visible': {
              outline: 'none',
              border: 'none',
              boxShadow: 'none',
            },
            // Hide scrollbar
            '&::-webkit-scrollbar': {
              height: '0px'
            },
          }}
        >
          <Box
            sx={{
              width: (timeRange === '12h' || timeRange === '6h') ? `${minChartWidth}px` : '100%',
              minWidth: '0px',
              height: '100%',
              outline: 'none',
              '&:focus': {
                outline: 'none',
              },
              '&:focus-visible': {
                outline: 'none',
              },
            }}
          >
            <ResponsiveContainer width="100%" height="100%">
              <LineChart 
                data={displayData}
                margin={{ 
                  top: 15, 
                  right: 15, 
                  left: 15, 
                  bottom: 0
                }}
              >
                <defs>
                  <linearGradient id="colorGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={lineColor} stopOpacity={0.3}/>
                    <stop offset="95%" stopColor={lineColor} stopOpacity={0}/>
                  </linearGradient>
                </defs>

                <CartesianGrid 
                  strokeDasharray="3 3" 
                  stroke="#e0e0e0"
                  vertical={false}
                />

                <XAxis
                  dataKey="position"
                  type="number"
                  domain={xAxisDomain}
                  ticks={(() => {
                    // Generate ticks based on time range
                    const ticks = [];
                    if (timeRange === '6h') {
                      // For 6h: Show all 24 hours (for scrolling), viewport will show current time to current time + 6 hours
                      // Generate all hourly ticks
                      for (let h = 0; h < 24; h++) {
                        ticks.push(h); // Position is just the hour when startHour = 0
                      }
                      // Add 11:59 PM as the last tick
                      ticks.push(23 + 59/60); // 11:59 PM
                    } else if (timeRange === '12h') {
                      // For 12h: Show all 24 hours ending at 11:59
                      for (let h = 0; h < 24; h++) {
                        const position = timeToPosition(`${h.toString().padStart(2, '0')}:00`, startHour);
                        ticks.push(position);
                      }
                      ticks.push(23 + 59/60); // 11:59 PM
                    } else {
                      // For 24h: Show ticks every 2 hours (12:00 AM, 2:00 AM, 4:00 AM, etc.)
                      for (let h = 0; h < 24; h += 2) {
                        ticks.push(h); // Position is just the hour when startHour = 0
                      }
                      ticks.push(23 + 59/60); // 11:59 PM
                    }
                    return ticks;
                  })()}
                  tickFormatter={(position) => {
                    // Convert position to hour format: 12AM, 1AM, 2AM, ... 12PM, 1PM, ... 12AM
                    // Handle 11:59 PM (23.983) specially - show as 12AM
                    if (Math.abs(position - (23 + 59/60)) < 0.01) {
                      return "12AM";
                    }
                    
                    const hour = Math.floor(position) + startHour;
                    // Convert 24-hour to 12-hour format
                    let displayHour = hour % 12;
                    if (displayHour === 0) {
                      displayHour = 12; // 0 and 12 both become 12
                    }
                    
                    // Determine AM/PM
                    const ampm = hour < 12 ? "AM" : "PM";
                    
                    // Format: 12AM, 1AM, 2AM, ... 12PM, 1PM, etc.
                    return `${displayHour}${ampm}`;
                  }}
                  tick={{ 
                    fontSize: window.innerWidth < 600 ? 8 : window.innerWidth < 900 ? 9 : 10, 
                    fill: '#666'
                  }}
                  interval={0}
                  angle={0}
                  textAnchor="middle"
                  height={window.innerWidth < 600 ? 60 : 50}
                  stroke="#999"
                  tickMargin={window.innerWidth < 600 ? 8 : 12}
                  allowDecimals={false}
                />

                {/* Hidden Y-Axis (for alignment only) */}
                <YAxis 
                  yAxisId="left"
                  domain={yAxisDomain}
                  hide={true}
                />

                <Tooltip
                  content={<EmployeeMovementTooltip startHour={startHour} />}
                  cursor={{ stroke: lineColor, strokeWidth: 1, strokeDasharray: '5 5' }}
                />

                {/* Step line chart */}
                <Line
                  yAxisId="left"
                  type="stepAfter"
                  dataKey="count"
                  stroke={lineColor}
                  strokeWidth={window.innerWidth < 600 ? 2 : 3}
                  dot={false}
                  activeDot={{ 
                    r: window.innerWidth < 600 ? 4 : 6, 
                    fill: lineColor,
                    stroke: '#fff',
                    strokeWidth: 2
                  }}
                  fill="url(#colorGradient)"
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Card
        sx={{
          maxWidth: "100%",
          width: "100%",
          height: "100%",
          background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
          transition: "all 0.3s ease-in-out",
          border: "1px solid rgba(64, 81, 138, 0.12)",
          borderRadius: 3,
          boxShadow: "0 10px 28px rgba(31, 45, 84, 0.12)",
          "&:hover": { boxShadow: "0 12px 32px rgba(0,0,0,0.18)" },
          overflow: "visible",
        }}
      >
      <CardContent sx={{ 
        p: { xs: 1.75, sm: 2.25, md: 2.5 },
        overflow: "visible",
        maxWidth: "100%",
      }}>
        {/* ---------- ROW 1: Title and Date Picker ---------- */}
        <Box 
          sx={{ 
            display: "flex", 
            mb: 2, 
            flexWrap: 'wrap', 
            gap: { xs: 1.5, sm: 2 }, 
            alignItems: { xs: "stretch", sm: "center" }, 
            justifyContent: "space-between" 
          }}
        >
          {/* Left: Icon + Title */}
          <Box sx={{ 
            display: "flex", 
            alignItems: "center", 
            gap: { xs: 1.25, sm: 2 },
            minWidth: 0
          }}>
            <Box
              sx={{
                p: { xs: 1.2, sm: 1.5 },
                background: "rgba(102,126,234,0.1)",
                borderRadius: 2,
                display: "flex",
                alignItems: "center"
              }}
            >
              <TrendingUp sx={{ color: "#667EEA", fontSize: { xs: 22, sm: 28 } }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 700, 
                  color: "#2d3748",
                  fontSize: { xs: "1.18rem", sm: "1.25rem" },
                  lineHeight: 1.25,
                }}
              >
                {title}
              </Typography>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: "text.secondary", 
                  display: { xs: 'none', sm: 'flex' },
                  alignItems: "center", 
                  gap: 1
                }}
              >
                <People sx={{ fontSize: 16 }} />
                Peak: {peakOccupancy} | Current: {currentPresent}
              </Typography>
            </Box>
          </Box>

          {/* Right: Date Picker */}
          <Box 
            sx={{ 
              display: "flex", 
              gap: 1, 
              flexWrap: "wrap",
              alignItems: "center",
              width: { xs: "100%", sm: "auto" },
              justifyContent: { xs: "flex-start", sm: "flex-end" },
              position: "relative",
              zIndex: 100,
            }}
          >
            <Box sx={{ width: { xs: "100%", sm: "auto" } }}>
              <SingleDatePicker
                selectedDate={selectedDate}
                onChange={handlePickerChange}
                maxDate={new Date()}
                disableFuture
              />
            </Box>
            
            <Button
              variant="contained"
              onClick={handleApply}
              disabled={loading}
              size="small"
              sx={{
                bgcolor: '#40518A',
                p:0.9,
                minWidth: { xs: "100%", sm: 88 },
                '&:hover': {
                  bgcolor: '#2d3961'
                },
                fontWeight: 600,
                boxShadow: "0 2px 8px rgba(102,126,234,0.3)"
              }}
            >
              Apply
            </Button>
          </Box>
        </Box>

        {/* ---------- ROW 2: Time Range Buttons and Data Info ---------- */}
        <Box 
          sx={{ 
            display: "flex", 
            mb: 2, 
            flexWrap: 'wrap', 
            gap: 1.25, 
            alignItems: "center", 
            justifyContent: { xs: "stretch", sm: "flex-end" } 
            
          }}
        >
          {/* Left: Time Range Buttons */}
          <ButtonGroup 
            variant="outlined" 
            size="small"
            sx={{
              width: { xs: "100%", sm: "auto" },
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              '& .MuiButton-root': {
                flex: { xs: 1, sm: "initial" },
                borderColor: '#473e8a',
                color: '#473e8a',
                fontWeight: 600,
                '&:hover': {
                  borderColor: '#2d3961',
                  background: 'rgba(70, 87, 165, 0.08)'
                }
              }
            }}
          >
            <Button
              onClick={() => handleTimeRangeChange('6h')}
              variant={timeRange === '6h' ? 'contained' : 'outlined'}
              sx={{
                bgcolor: timeRange === '6h' ? '#40518A !important' : 'transparent',
                color: timeRange === '6h' ? '#fff !important' : '#40518A',
                '&:hover': {
                  bgcolor: timeRange === '6h' ? '#40518A !important' : 'rgba(102,126,234,0.08)',
                }
              }}
            >
              6H
            </Button>
            <Button
              onClick={() => handleTimeRangeChange('12h')}
              variant={timeRange === '12h' ? 'contained' : 'outlined'}
              sx={{
                bgcolor: timeRange === '12h' ? '#40518A !important' : 'transparent',
                color: timeRange === '12h' ? '#fff !important' : '#667EEA',
                '&:hover': {
                  bgcolor: timeRange === '12h' ? '#473e8a !important' : 'rgba(102,126,234,0.08)',
                }
              }}
            >
              12H
            </Button>
            <Button
              onClick={() => handleTimeRangeChange('24h')}
              variant={timeRange === '24h' ? 'contained' : 'outlined'}
              sx={{
                bgcolor: timeRange === '24h' ? '#473e8a !important' : 'transparent',
                color: timeRange === '24h' ? '#fff !important' : '#667EEA',
                '&:hover': {
                  bgcolor: timeRange === '24h' ? '#40518A !important' : 'rgba(102,126,234,0.08)',
                }
              }}
            >
              24H
            </Button>
          </ButtonGroup>

        </Box>

        {/* Mobile Stats Row - Only visible on mobile */}
        <Box 
          sx={{ 
            width: "100%",
            display: { xs: 'flex', sm: 'none' },
            gap: 1.5,
            mb: 2,
            justifyContent: 'space-around',
            bgcolor: 'rgba(64,81,138,0.06)',
            border: '1px solid rgba(64,81,138,0.12)',
            p: 1.5,
            borderRadius: 2
          }}
        >
          <Box sx={{ textAlign: 'center', flex: 1 }}>
            <Typography variant="caption" sx={{ color: "text.secondary", display: 'block' }}>
              Peak
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700, color: '#667EEA' }}>
              {peakOccupancy}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'center', flex: 1 }}>
            <Typography variant="caption" sx={{ color: "text.secondary", display: 'block' }}>
              Current
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700, color: '#667EEA' }}>
              {currentPresent}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'center', flex: 1 }}>
            <Typography variant="caption" sx={{ color: "text.secondary", display: 'block' }}>
              Points
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700, color: '#667EEA' }}>
              {displayData.length}
            </Typography>
          </Box>
        </Box>

        {(pickerError || error) && (
          <Box 
            sx={{ 
              mb: 2, 
              p: 1.5, 
              bgcolor: 'rgba(244, 67, 54, 0.1)', 
              borderRadius: 2,
              border: '1px solid rgba(244, 67, 54, 0.3)'
            }}
          >
            <Typography variant="body2" color="error" sx={{ fontWeight: 600 }}>
              {pickerError || error}
            </Typography>
          </Box>
        )}

        {/* ---------- LINE CHART ---------- */}
        {renderChartContent()}
      </CardContent>
    </Card>
  );
};

export default LineChartCard;
